import { useCallback, useEffect, useRef, useState } from 'react';
import {
  IMinDetails,
  IProductDelivery,
  IShipmentData,
  SHIPMENT_STATUS_ID
} from '../../../../types/orders';
import {
  finishDelivery,
  massUpdateShipmentStatus
} from '../../../../services/traking.services';
import {
  createTemporaryStorageForShipment,
  updateTemporaryStorageForShipment
} from '../../../../services/orders';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../utils/displayNudosStandardNotifications';
import { extractProductData } from 'utils/extractProductData';
import {
  getSelectedProductsCount,
  getTransitToWarehouseTrackingInitialValues
} from 'utils/shipmentStatusTransition.utils';
import { getSerialErrorsFromApiResponse } from '../../../../utils/getSerialErrorsFromApiResponse';
import { IMassiveDeliveryInProgress } from '../../../../types/traking';
import { TEMPORARY_STORAGE_REASON_TYPE_ID } from '../../../../types/orders';
import { FALLBACK_FLAG_IMAGE_URL } from '../../../../constants/constants';
import { TOOL_STATUS_NAME } from 'types/tools';
import {
  displayConfigurationTransitionBlockedNotification,
  evaluateShipmentTransitToDestinationTransition,
  handleConfigurationTransitionBlockedApiError,
  SHIPMENT_TRANSIT_TO_DESTINATION_BLOCK_MESSAGE
} from '../../../../utils/productConfigurationSummary.utils';
import useOnlyDevelopers from '../../../../state/setOnlyDevelopers';

export interface IShipmentStatusTransitionConfig {
  targetStatusId: number;
  sourceStatusId?: number;
  modalTitle: string;
  primaryButtonText: string;
  headerTitle: string;
  customAlertMessage?: string;
  successMessage: React.ReactElement;
  requiresDeliveryDate?: boolean;
  requiresTransitTrackingInfo?: boolean;
  requiresSerials?: boolean;
  secondStepVerificationText?: string | ((params: { destinationName: string }) => string);
  skipShipmentStatusMassUpdate?: boolean;
}

export interface IUseShipmentStatusTransitionParams {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  transitionConfig: IShipmentStatusTransitionConfig;
  updateWithoutLoaders?: () => void;
  onTransitionConfirmed?: (shipments: IShipmentData[]) => void;
  receiverName?: string;
  shipmentData?: IShipmentData;
  products?: IProductDelivery[];
  selectedShipments?: IShipmentData[];
  orderId?: string;
  warehouseStopReasons?: TEMPORARY_STORAGE_REASON_TYPE_ID[];
}

/**
 * Hook con la lógica del modal de transición de estado de envío.
 * Gestiona lista de envíos a cambiar, confirmación de transición y valores derivados (modo legacy/masivo, destino, etc.).
 * Precarga fecha y tracking del paso «Preparación → Tránsito a bodega» desde datos ya guardados en el envío.
 * Con `skipShipmentStatusMassUpdate` solo persiste almacenaje temporal (edición de ETA desde tracking).
 */
export function useShipmentStatusTransition({
  setShowModal,
  transitionConfig,
  updateWithoutLoaders,
  onTransitionConfirmed,
  receiverName,
  shipmentData,
  products,
  selectedShipments,
  orderId,
  warehouseStopReasons
}: IUseShipmentStatusTransitionParams) {
  const [loading, setLoading] = useState<boolean>(false);
  const [listOfShipmentsToChangeStatus, setListOfShipmentsToChangeStatus] = useState<IShipmentData[]>([]);
  const [errorList, setErrorList] = useState<IMinDetails[] | undefined>([]);
  const [hasProductSelectorErrors, setHasProductSelectorErrors] = useState<boolean>(false);
  const [deliveryDate, setDeliveryDate] = useState<string | undefined>(undefined);
  const [estimatedEntryDate, setEstimatedEntryDate] = useState<string | undefined>(undefined);
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [trackingLink, setTrackingLink] = useState<string>('');
  const [showDeliveryFinalConfirm, setShowDeliveryFinalConfirm] = useState<boolean>(false);

  const transitToWarehouseHydrationKeyRef = useRef<string | null>(null);
  const initialEstimatedEntryDateRef = useRef<string | undefined>(undefined);
  const initialTrackingNumberRef = useRef<string>('');
  const initialTrackingLinkRef = useRef<string>('');
  const isEditOnlyFlow = transitionConfig.skipShipmentStatusMassUpdate === true;
  const { isOnlyDevsLogistics } = useOnlyDevelopers();
  const isLegacyMode = !selectedShipments?.length && !!shipmentData;
  const shipmentsToUse =
    isLegacyMode && shipmentData
      ? [{ ...shipmentData, products: products || [] }]
      : (selectedShipments?.filter(
        s => !transitionConfig.sourceStatusId || s.statusId === transitionConfig.sourceStatusId
      ) || []);
  const isBulkAction = shipmentsToUse.length > 1;
  const firstShipment = shipmentsToUse[0];
  const countryTimeZone = firstShipment?.country?.timeZone ?? undefined;
  const destinationName =
    receiverName?.trim() ||
    firstShipment?.receiverInfo?.receiverName?.trim() ||
    TOOL_STATUS_NAME.WAREHOUSE;

  const requiresDeliveryDate = transitionConfig.requiresDeliveryDate === true;
  const requiresTransitTrackingInfo = transitionConfig.requiresTransitTrackingInfo === true;
  const isTransitionToDelivered = transitionConfig.targetStatusId === SHIPMENT_STATUS_ID.DELIVERED;

  const secondStepVerificationTextResolved =
    transitionConfig.secondStepVerificationText == null
      ? undefined
      : typeof transitionConfig.secondStepVerificationText === 'function'
        ? transitionConfig.secondStepVerificationText({ destinationName })
        : transitionConfig.secondStepVerificationText;

  const hasSecondStepConfirm = isTransitionToDelivered && !!secondStepVerificationTextResolved;

  const disableButton = () => {
    const missingDeliveryDate = requiresDeliveryDate && !deliveryDate?.trim();
    const missingEstimatedEntryDate =
      requiresTransitTrackingInfo && !estimatedEntryDate?.trim();
    const currentEstimatedEntry = estimatedEntryDate?.trim() ?? '';
    const initialEstimatedEntry = initialEstimatedEntryDateRef.current?.trim() ?? '';
    const currentTrackingNumber = trackingNumber?.trim() ?? '';
    const currentTrackingLink = trackingLink?.trim() ?? '';
    const trackingFieldsUnchangedInEditFlow =
      isEditOnlyFlow &&
      requiresTransitTrackingInfo &&
      currentEstimatedEntry === initialEstimatedEntry &&
      currentTrackingNumber === initialTrackingNumberRef.current &&
      currentTrackingLink === initialTrackingLinkRef.current;
    const hasNoSerialsSelected = getSelectedProductsCount(listOfShipmentsToChangeStatus) === 0;

    return (
      hasProductSelectorErrors ||
      hasNoSerialsSelected ||
      missingDeliveryDate ||
      missingEstimatedEntryDate ||
      trackingFieldsUnchangedInEditFlow
    );
  };

  const handleCloseClick = () => {
    if (loading) return;
    setShowDeliveryFinalConfirm(false);
    setShowModal(false);
  };

  const handlePrimaryButtonClick = () => {
    if (hasSecondStepConfirm) {
      setShowDeliveryFinalConfirm(true);
      return;
    }
    confirmTransition();
  };

  const handleConfirmDeliveryFinal = async () => {
    await confirmTransition();
  };

  /**
   * Valida que todos los envíos seleccionados puedan pasar a Tránsito al destino.
   */
  const validateTransitToDestinationForSelectedShipments = async (): Promise<boolean> => {
    if (!isOnlyDevsLogistics) return true;

    for (const shipment of listOfShipmentsToChangeStatus) {
      const { isBlocked } = await evaluateShipmentTransitToDestinationTransition(shipment.shipmentId);

      if (isBlocked) {
        displayConfigurationTransitionBlockedNotification({
          displayErrorNotification,
          mainMessage: SHIPMENT_TRANSIT_TO_DESTINATION_BLOCK_MESSAGE
        });
        return false;
      }
    }

    return true;
  };

  const confirmTransition = async () => {
    if (loading) return;
    setLoading(true);
    setErrorList([]);

    try {
      if (getSelectedProductsCount(listOfShipmentsToChangeStatus) === 0) {
        displayErrorNotification();
        return;
      }

      const isTransitionToDelivered =
        transitionConfig.targetStatusId === SHIPMENT_STATUS_ID.DELIVERED;

      if (isTransitionToDelivered) {
        const bodyData: IMassiveDeliveryInProgress[] = listOfShipmentsToChangeStatus.map(shipment => {
          return {
            shipmentId: shipment.shipmentId,
            deliveryDate: deliveryDate?.trim() || null,
            newSerials: extractProductData(shipment?.products)
          };
        });

        const deliveryResponse = await massUpdateShipmentStatus(
          transitionConfig.targetStatusId,
          bodyData
        );
        const serialErrors = getSerialErrorsFromApiResponse(deliveryResponse);

        if (serialErrors.length > 0) {
          setShowDeliveryFinalConfirm(false);
          setErrorList(serialErrors);
          return;
        }

        if (orderId) {
          await finishDelivery(orderId);
        }
      } else {
        const isTransitionToTransitToWarehouse =
          transitionConfig.targetStatusId === SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE;

        const hasWarehouseStopReasons =
          isTransitionToTransitToWarehouse &&
          warehouseStopReasons != null &&
          warehouseStopReasons.length > 0 &&
          requiresTransitTrackingInfo &&
          !!estimatedEntryDate?.trim();

        if (hasWarehouseStopReasons) {
          if (transitionConfig.skipShipmentStatusMassUpdate) {
            const missingWts = listOfShipmentsToChangeStatus.some(s => !s.warehouseTemporaryStorage);
            if (missingWts) {
              displayErrorNotification();
              return;
            }
          }
          const estimatedEntryDateValue = estimatedEntryDate?.trim() || '';
          const trackingCode = trackingNumber?.trim() || undefined;
          const trackingUrl = trackingLink?.trim() || undefined;

          for (const shipment of listOfShipmentsToChangeStatus) {
            const temporaryStorageBody = {
              shipmentId: shipment.shipmentId,
              reasons: warehouseStopReasons,
              estimatedEntryDate: estimatedEntryDateValue,
              trackingCode,
              trackingUrl,
              newSerials: extractProductData(shipment?.products) ?? []
            };

            const hasExistingTemporaryStorage = !!shipment.warehouseTemporaryStorage;

            const storageResponse = transitionConfig.skipShipmentStatusMassUpdate
              ? await updateTemporaryStorageForShipment(temporaryStorageBody)
              : hasExistingTemporaryStorage
                ? await updateTemporaryStorageForShipment(temporaryStorageBody)
                : await createTemporaryStorageForShipment(temporaryStorageBody);

            const storageErrors = getSerialErrorsFromApiResponse(storageResponse);

            if (storageErrors.length > 0) {
              setShowDeliveryFinalConfirm(false);
              setErrorList(storageErrors);
              return;
            }
          }
        }

        if (transitionConfig.skipShipmentStatusMassUpdate) {
          if (!hasWarehouseStopReasons) {
            displayErrorNotification();
            return;
          }
          await updateWithoutLoaders?.();
          onTransitionConfirmed?.(listOfShipmentsToChangeStatus);
          setShowModal(false);
          displaySuccessNotification({
            customJSXMessage: transitionConfig.successMessage
          });
          return;
        }

        const isTransitionToTransitToDestination =
          transitionConfig.targetStatusId === SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION;

        if (isTransitionToTransitToDestination) {
          const canProceed = await validateTransitToDestinationForSelectedShipments();
          if (!canProceed) return;
        }

        const bodyMassive: IMassiveDeliveryInProgress[] = listOfShipmentsToChangeStatus.map(shipment => {
          const item: IMassiveDeliveryInProgress = {
            shipmentId: shipment.shipmentId,
            newSerials: extractProductData(shipment?.products)
          };

          if (requiresTransitTrackingInfo) {
            item.estimatedDeliveryDate = estimatedEntryDate?.trim() || '';
            item.trackingNumber = trackingNumber?.trim() || '';
            item.trackingLink = trackingLink?.trim() || '';
          }

          return item;
        });
        const statusResponse = await massUpdateShipmentStatus(transitionConfig.targetStatusId, bodyMassive);
        const statusErrors = getSerialErrorsFromApiResponse(statusResponse);

        if (statusErrors.length > 0) {
          setShowDeliveryFinalConfirm(false);
          setErrorList(statusErrors);
          return;
        }
      }

      await updateWithoutLoaders?.();
      onTransitionConfirmed?.(listOfShipmentsToChangeStatus);
      setShowModal(false);

      displaySuccessNotification({
        customJSXMessage: transitionConfig.successMessage
      });
    } catch (error) {
      const handledConfigurationBlock =
        isOnlyDevsLogistics &&
        handleConfigurationTransitionBlockedApiError(
          error,
          SHIPMENT_TRANSIT_TO_DESTINATION_BLOCK_MESSAGE,
          displayErrorNotification
        );

      if (!handledConfigurationBlock) {
        displayErrorNotification();
      }
    } finally {
      setLoading(false);
    }
  };


  const hydrateTransitToWarehouseTrackingFields = () => {
    if (!requiresTransitTrackingInfo || transitionConfig.targetStatusId !== SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE) {
      return;
    }
    if (!firstShipment) return;

    const values = getTransitToWarehouseTrackingInitialValues(firstShipment);
    const key = [
      firstShipment.shipmentId,
      firstShipment.warehouseTemporaryStorage?.id ?? '',
      firstShipment.warehouseTemporaryStorage?.estimatedEntryDate ?? '',
      firstShipment.warehouseTemporaryStorage?.trackingCode ?? '',
      firstShipment.warehouseTemporaryStorage?.trackingUrl ?? '',
      firstShipment.trackingInfo?.currentEstimatedDeliverDate ?? '',
      firstShipment.trackingInfo?.estimatedDeliveryDate ?? '',
      firstShipment.trackingInfo?.trackerNumber ?? '',
      firstShipment.trackingInfo?.trackingLink ?? ''
    ].join('|');

    if (transitToWarehouseHydrationKeyRef.current === key) return;

    const hasData =
      !!values.estimatedEntryDate?.trim() ||
      !!values.trackingNumber?.trim() ||
      !!values.trackingLink?.trim();

    if (!hasData) {
      transitToWarehouseHydrationKeyRef.current = key;
      if (isEditOnlyFlow) {
        initialEstimatedEntryDateRef.current = values.estimatedEntryDate?.trim() || undefined;
        initialTrackingNumberRef.current = values.trackingNumber?.trim() ?? '';
        initialTrackingLinkRef.current = values.trackingLink?.trim() ?? '';
      }
      return;
    }

    setEstimatedEntryDate(values.estimatedEntryDate);
    setTrackingNumber(values.trackingNumber);
    setTrackingLink(values.trackingLink);
    initialEstimatedEntryDateRef.current = values.estimatedEntryDate?.trim() || undefined;
    initialTrackingNumberRef.current = values.trackingNumber?.trim() ?? '';
    initialTrackingLinkRef.current = values.trackingLink?.trim() ?? '';
    transitToWarehouseHydrationKeyRef.current = key;
  };

  const getInitialListOfShipmentsToChangeStatus = useCallback((): IShipmentData[] | null => {
    if (isLegacyMode && shipmentData && (products?.length ?? 0) > 0) {
      return [{ ...shipmentData, products: products || [] }];
    }

    if (selectedShipments?.length) {
      const filtered = selectedShipments.filter(
        s => !transitionConfig.sourceStatusId || s.statusId === transitionConfig.sourceStatusId
      );
      return filtered.length > 0 ? filtered : null;
    }

    return null;
  }, [isLegacyMode, shipmentData, products, selectedShipments, transitionConfig.sourceStatusId]);

  useEffect(() => {
    const initialList = getInitialListOfShipmentsToChangeStatus();
    if (initialList !== null && !isEditOnlyFlow) {
      setListOfShipmentsToChangeStatus(initialList);
    }
  }, [getInitialListOfShipmentsToChangeStatus, isEditOnlyFlow]);

  useEffect(() => {
    hydrateTransitToWarehouseTrackingFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresTransitTrackingInfo, transitionConfig.targetStatusId, isEditOnlyFlow, firstShipment]);

  return {
    loading,
    listOfShipmentsToChangeStatus,
    setListOfShipmentsToChangeStatus,
    errorList,
    setErrorList,
    hasProductSelectorErrors,
    setHasProductSelectorErrors,
    isLegacyMode,
    shipmentsToUse,
    isBulkAction,
    firstShipment,
    countryTimeZone,
    destinationName,
    fallbackFlagImageUrl: FALLBACK_FLAG_IMAGE_URL,
    disableButton,
    confirmTransition,
    handleCloseClick,
    handlePrimaryButtonClick,
    handleConfirmDeliveryFinal,
    showDeliveryFinalConfirm,
    setShowDeliveryFinalConfirm,
    secondStepVerificationText: secondStepVerificationTextResolved,
    requiresDeliveryDate,
    deliveryDate,
    setDeliveryDate,
    requiresTransitTrackingInfo,
    estimatedEntryDate,
    setEstimatedEntryDate,
    trackingNumber,
    setTrackingNumber,
    trackingLink,
    setTrackingLink
  };
}
